import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ageAt, formatAge, parseIsoDay } from '@wodi/domain';
import type { PrescriptionLineView, PrescriptionPrintView } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  PrintTable,
  printSheet,
} from '@wodi/ui';
import { useRef } from 'react';

import { ApiError } from '../api/client.ts';
import {
  markPrescriptionPrinted,
  prescribingKeys,
  prescriptionPrintQuery,
} from '../api/prescribing.ts';
import { formatInstant } from '../patients/format.ts';
import { quantityWords } from './draft.ts';
import styles from './prescribing.module.css';

/**
 * The prescription's words, keyed so a reviewed Kiswahili version can be added without
 * touching the template (DESIGN.md §9; TODO(verify-spec): the translation).
 */
export const PRESCRIPTION_TEXT = {
  en: {
    title: 'Prescription',
    copy: (n: number) => `Copy ${String(n)}`,
    number: 'Prescription no.',
    date: 'Date',
    patient: 'Patient',
    patientNo: 'Patient no.',
    sexAge: 'Sex, age',
    weight: 'Weight',
    allergies: 'Allergies',
    noKnownAllergies: 'No known allergies',
    allergiesNotRecorded: 'Allergies not recorded',
    drug: 'Medicine',
    directions: 'Directions',
    quantity: 'Quantity',
    buyOutside: 'Not stocked here: buy outside',
    noSubstitution: 'Do not substitute',
    giveInClinic: 'Give in clinic',
    prescriber: 'Prescriber',
    licenceNotRecorded: 'Licence not recorded',
    signature: 'Signature',
    stopped: 'Stopped',
    cancelled: 'Cancelled',
    enteredInError: 'Entered in error',
  },
} as const;

const T = PRESCRIPTION_TEXT.en;

/** "1 capsule by mouth, three times a day, for 5 days", from the stored snapshot. */
export function directions(line: PrescriptionLineView): string {
  const parts = [
    `${line.dose.value} ${line.dose.unitDisplay}`,
    line.route.display.toLowerCase(),
    line.frequency.display.toLowerCase(),
  ];
  if (line.maxDosesPerDay !== null)
    parts.push(`at most ${String(line.maxDosesPerDay)} times in 24 hours`);
  if (line.duration !== null)
    parts.push(`for ${String(line.duration.value)} ${line.duration.unit}`);
  return parts.join(', ');
}

function ageWords(print: PrescriptionPrintView): string {
  if (print.patient.birthDate === null) return 'age not recorded';
  const day = parseIsoDay(print.patient.birthDate);
  if (!day.ok) return 'age not recorded';
  const text = formatAge(ageAt(day.value, new Date(print.prescription.signedAt)));
  return print.patient.birthDateEstimated ? `${text} (estimated)` : text;
}

const STATUS_WORDS: Record<PrescriptionLineView['status'], string | null> = {
  active: null,
  stopped: T.stopped,
  cancelled: T.cancelled,
  entered_in_error: T.enteredInError,
};

export function PrescriptionSheet({
  print,
  sheetRef,
}: {
  print: PrescriptionPrintView;
  sheetRef?: React.Ref<HTMLElement>;
}) {
  const rx = print.prescription;
  const allergies =
    print.allergyState === 'allergies'
      ? print.allergies.join(', ')
      : print.allergyState === 'no_known_allergies'
        ? T.noKnownAllergies
        : T.allergiesNotRecorded;
  return (
    <PrintSheet paper="a5" label={T.title} {...(sheetRef === undefined ? {} : { ref: sheetRef })}>
      <PrintHeader
        facility={print.facility.name}
        title={print.copy > 1 ? `${T.title}, ${T.copy(print.copy)}` : T.title}
      />
      <PrintLines
        items={[
          { label: T.number, value: rx.prescriptionNumber, identifier: true },
          { label: T.date, value: formatInstant(rx.signedAt) },
          { label: T.patient, value: print.patient.name },
          { label: T.patientNo, value: print.patient.patientNumber, identifier: true },
          { label: T.sexAge, value: `${print.patient.sexDisplay}, ${ageWords(print)}` },
          ...(print.weightKg === null ? [] : [{ label: T.weight, value: `${print.weightKg} kg` }]),
          { label: T.allergies, value: allergies },
        ]}
      />
      <PrintTable>
        <thead>
          <tr>
            <th>{T.drug}</th>
            <th>{T.directions}</th>
            <th>{T.quantity}</th>
          </tr>
        </thead>
        <tbody>
          {rx.lines.map((line) => {
            const status = STATUS_WORDS[line.status];
            const notes = [
              line.serviceItemId === null ? T.buyOutside : null,
              line.substitutionAllowed ? null : T.noSubstitution,
              line.giveInClinic ? T.giveInClinic : null,
              status,
            ].filter((n): n is string => n !== null);
            return (
              <tr key={line.id}>
                <td>
                  {line.drugDisplay}
                  {notes.length === 0 ? null : <div>{notes.join('. ')}</div>}
                </td>
                <td>
                  {directions(line)}
                  {line.instructions === null ? null : <div>{line.instructions}</div>}
                </td>
                <td>{quantityWords(line.quantity, line.quantityUnit)}</td>
              </tr>
            );
          })}
        </tbody>
      </PrintTable>
      <PrintFooter>
        {`${T.prescriber}: ${rx.prescriberDisplay}`}
        <br />
        {rx.prescriberLicenceDisplay ?? T.licenceNotRecorded}
        <br />
        {`${T.signature}: ____________________`}
      </PrintFooter>
    </PrintSheet>
  );
}

/** Loads the print data, shows the sheet and prints it; every print is counted and audited. */
export function PrescriptionPrinter({
  prescriptionId,
  visitId,
}: {
  prescriptionId: string;
  visitId: string;
}) {
  const queryClient = useQueryClient();
  const sheet = useRef<HTMLElement>(null);
  const print = useQuery(prescriptionPrintQuery(prescriptionId));
  const printed = useMutation({
    mutationFn: () => markPrescriptionPrinted(prescriptionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['prescriptions', prescriptionId, 'print'] });
      void queryClient.invalidateQueries({ queryKey: prescribingKeys.visit(visitId) });
    },
  });
  if (print.isPending) return <LoadingBlock label="Loading the prescription" />;
  if (print.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void print.refetch()}>Try again</Button>}>
        {print.error instanceof ApiError
          ? print.error.message
          : 'The prescription could not load. Try again.'}
      </Notice>
    );
  }
  // The print button comes first so it is in view without scrolling; the A5 sheet below is
  // a preview that scrolls sideways inside the dialog on screen and prints at full size.
  return (
    <div className={styles.printer}>
      <Button
        variant="primary"
        onPress={() => {
          if (sheet.current === null) return;
          printSheet(sheet.current);
          printed.mutate();
        }}
      >
        Print prescription
      </Button>
      <div className={styles.preview}>
        <PrescriptionSheet print={print.data} sheetRef={sheet} />
      </div>
    </div>
  );
}
